/* eslint-disable no-new */
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'

// Configuración de Firebase
const firebaseConfig = {
	apiKey: 'AIzaSyD1wZrLro8LxLu5B9jBczEzK_Y20Qz-iPA',
	authDomain: 'salud-continua.firebaseapp.com',
	projectId: 'salud-continua',
	storageBucket: 'salud-continua.firebasestorage.app',
	messagingSenderId: '48109178746',
	appId: '1:48109178746:web:d7056611ca65eadca6d0c3'
}

// Rutas donde Firebase Messaging no debe ejecutarse
const PROHIBITED_ROUTES = ['/par-q-public', '/gestor', '/auth-aliados']

// Función para verificar si estamos en una ruta prohibida
const isProhibitedRoute = () => {
	const currentPath = window.location.pathname
	return PROHIBITED_ROUTES.some(route => currentPath.includes(route))
}

// Singleton para asegurar una sola instancia de Firebase
let app = null
let messaging = null
let swRegistration = null

// Función para inicializar Firebase solo una vez
export const initializeFirebase = () => {
	if (!app) {
		app = initializeApp(firebaseConfig)
	}
	return app
}

// Función para limpiar registros de service worker duplicados
const cleanupServiceWorkers = async (forceCleanup = false) => {
	if (!('serviceWorker' in navigator)) return null

	try {
		// Verificar todos los service workers registrados
		const registrations = await navigator.serviceWorker.getRegistrations()

		// Desregistrar service workers según la configuración
		for (const reg of registrations) {
			const isFirebaseSW = reg.active?.scriptURL.includes('firebase-messaging-sw.js')

			if (forceCleanup && isFirebaseSW) {
				await reg.unregister()
			} else if (reg.scope !== window.location.origin + '/' && isFirebaseSW) {
				// Limpieza normal: solo duplicados o con scope incorrecto
				await reg.unregister()
			}
		}

		return true
	} catch (error) {
		console.error('Error al limpiar service workers:', error)
		return false
	}
}

// Inicializar messaging de forma segura
export const initializeMessaging = async () => {
	if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
		return null
	}

	// Verificar si estamos en rutas donde no debe ejecutarse Firebase messaging
	if (isProhibitedRoute()) {
		// Desregistrar cualquier service worker de Firebase existente
		await cleanupServiceWorkers(true) // true = forzar limpieza completa
		return null
	}

	// Asegurar que Firebase está inicializado
	initializeFirebase()

	// Si ya tenemos messaging inicializado, devolverlo
	if (messaging) {
		return messaging
	}

	try {
		// Limpieza previa de service workers
		await cleanupServiceWorkers()

		// Verificar si ya existe un service worker con el scope correcto
		const registrations = await navigator.serviceWorker.getRegistrations()

		swRegistration = registrations.find(
			reg =>
				reg.scope === window.location.origin + '/' &&
				reg.active?.scriptURL.includes('firebase-messaging-sw.js')
		)

		if (!swRegistration) {
			// Registrar un nuevo service worker
			swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
				scope: '/'
			})

			// Esperar a que esté activo
			if (swRegistration.installing) {
				await new Promise(resolve => {
					const listener = () => {
						if (swRegistration.active) {
							// Verificar que installing aún existe antes de remover el listener
							if (swRegistration.installing) {
								swRegistration.installing.removeEventListener('statechange', listener)
							}
							resolve()
						}
					}
					swRegistration.installing.addEventListener('statechange', listener)
				})
			}
		}

		// Inicializar messaging
		messaging = getMessaging(app)
		return messaging
	} catch (err) {
		console.error('Error al inicializar Firebase Messaging:', err)
		return null
	}
}

// Registro de mensajes procesados para evitar duplicados
const processedMessages = new Set()

// Función para solicitar permiso y obtener el token FCM
export const requestForToken = async () => {
	// Verificar rutas prohibidas antes de intentar inicializar
	if (isProhibitedRoute()) {
		return null
	}

	await initializeMessaging()

	if (!messaging || !swRegistration) {
		console.error('Messaging o Service Worker no disponibles')
		return null
	}

	try {
		// Solicitar token con VAPID key
		const currentToken = await getToken(messaging, {
			serviceWorkerRegistration: swRegistration
		})

		if (currentToken) {
			return currentToken
		} else {
			console.warn('No se pudo obtener el token. Solicita permiso para generarlo.')
			return null
		}
	} catch (err) {
		console.error('Error al recuperar el token FCM:', err)
		return null
	}
}

// Función para manejar mensajes en primer plano
export const onMessageListener = () => {
	// Verificar rutas prohibidas
	if (isProhibitedRoute()) {
		return Promise.resolve(() => {})
	}

	if (!messaging) {
		return Promise.resolve(() => {})
	}

	let unsubscribe = null

	// Solo configuramos el listener si no existe ya
	if (!unsubscribe) {
		unsubscribe = onMessage(messaging, payload => {
			// Generar un ID único para este mensaje
			const messageId =
				payload.data?.messageId || payload.fcmMessageId || `${Date.now()}-${Math.random()}`

			// Si ya procesamos este mensaje, ignorarlo
			if (processedMessages.has(messageId)) {
				return payload
			}

			// Registrar este mensaje
			processedMessages.add(messageId)

			// Limitar el tamaño del set
			if (processedMessages.size > 100) {
				const firstItem = processedMessages.values().next().value
				processedMessages.delete(firstItem)
			}

			// Opcional: mostrar notificación nativa en primer plano
			if (Notification.permission === 'granted' && payload?.notification) {
				const { title, body, icon } = payload.notification
				new Notification(title || 'Nueva notificación', {
					body: body || '',
					icon: icon || '/favicon.ico',
					tag: messageId // Usar messageId como tag para evitar duplicados
				})
			}

			return payload
		})
	}

	return () => {
		if (unsubscribe && typeof unsubscribe === 'function') {
			unsubscribe()
			unsubscribe = null
		}
	}
}

// Función para limpiar Firebase en rutas prohibidas (llamar manualmente si es necesario)
export const cleanupFirebaseInProhibitedRoutes = async () => {
	if (isProhibitedRoute()) {
		await cleanupServiceWorkers(true)

		// Resetear variables globales
		messaging = null
		swRegistration = null

		return true
	}
	return false
}

// Inicializar Firebase al importar el módulo
initializeFirebase()

export { app, messaging, swRegistration }
